import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus } from 'lucide-react';

const QCInspection = () => {
  const [qcRecords, setQcRecords] = useState([]);
  const [mrns, setMrns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    mrn_id: '',
    overall_result: 'Pass',
    remarks: '',
    items: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [qcRes, mrnsRes] = await Promise.all([
        api.get('/purchase/qc'),
        api.get('/purchase/mrn')
      ]);
      setQcRecords(qcRes.data);
      setMrns(mrnsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectMRN = async (mrnId) => {
    const mrn = mrns.find(m => m.id === mrnId);
    if (mrn) {
      const items = mrn.items.map(item => ({
        sku: item.sku,
        name: item.name,
        received_quantity: item.received_quantity,
        inspected_quantity: item.received_quantity,
        accepted_quantity: item.accepted_quantity,
        rejected_quantity: item.rejected_quantity,
        rejection_reason: '',
        test_results: {}
      }));
      setFormData({ ...formData, mrn_id: mrnId, items });
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/purchase/qc', formData);
      setShowModal(false);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create QC inspection');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-warning',
      passed: 'badge-success',
      failed: 'badge-danger'
    };
    return `badge ${badges[status] || 'badge-info'}`;
  };

  return (
    <div data-testid="qc-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>QC Inspection</h1>
        <button onClick={() => setShowModal(true)} className="btn btn-primary" data-testid="create-qc-btn">
          <Plus size={16} />
          Create Inspection
        </button>
      </div>

      <div className="card">
        {loading ? (
          <div data-testid="loading-indicator">Loading QC records...</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>QC #</th>
                  <th>MRN #</th>
                  <th>PO #</th>
                  <th>Items</th>
                  <th>Result</th>
                  <th>Status</th>
                  <th>Inspected Date</th>
                </tr>
              </thead>
              <tbody>
                {qcRecords.map((qc) => (
                  <tr key={qc.id} data-testid={`qc-row-${qc.qc_number}`}>
                    <td className="font-mono">{qc.qc_number}</td>
                    <td className="font-mono">{qc.mrn_number}</td>
                    <td className="font-mono">{qc.po_id}</td>
                    <td>{qc.items?.length || 0} items</td>
                    <td>{qc.overall_result}</td>
                    <td>
                      <span className={getStatusBadge(qc.status)} data-testid={`status-${qc.qc_number}`}>
                        {qc.status}
                      </span>
                    </td>
                    <td className="text-sm">{new Date(qc.inspected_date).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="qc-modal">
          <div className="modal-content" style={{ maxWidth: '1000px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>QC Inspection</h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">MRN *</label>
                    <select
                      className="form-select"
                      value={formData.mrn_id}
                      onChange={(e) => selectMRN(e.target.value)}
                      required
                      data-testid="mrn-select"
                    >
                      <option value="">Select MRN...</option>
                      {mrns.map((mrn) => (
                        <option key={mrn.id} value={mrn.id}>
                          {mrn.mrn_number} - {mrn.vendor_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Overall Result *</label>
                    <select
                      className="form-select"
                      value={formData.overall_result}
                      onChange={(e) => setFormData({ ...formData, overall_result: e.target.value })}
                      required
                      data-testid="result-select"
                    >
                      <option value="Pass">Pass</option>
                      <option value="Partial Pass">Partial Pass</option>
                      <option value="Fail">Fail</option>
                    </select>
                  </div>
                </div>

                {formData.items.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Inspection Items</label>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', fontSize: '12px' }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '4px' }}>SKU</th>
                            <th style={{ padding: '4px' }}>Name</th>
                            <th style={{ padding: '4px' }}>Received</th>
                            <th style={{ padding: '4px' }}>Inspected</th>
                            <th style={{ padding: '4px' }}>Accepted</th>
                            <th style={{ padding: '4px' }}>Rejected</th>
                            <th style={{ padding: '4px' }}>Rejection Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.items.map((item, index) => (
                            <tr key={index}>
                              <td style={{ padding: '4px' }} className="font-mono">{item.sku}</td>
                              <td style={{ padding: '4px' }}>{item.name}</td>
                              <td style={{ padding: '4px' }} className="tabular-nums">{item.received_quantity}</td>
                              <td style={{ padding: '4px' }}>
                                <input
                                  type="number"
                                  className="form-input"
                                  value={item.inspected_quantity}
                                  onChange={(e) => updateItem(index, 'inspected_quantity', parseInt(e.target.value) || 0)}
                                  required
                                  style={{ width: '70px', fontSize: '12px', padding: '4px' }}
                                />
                              </td>
                              <td style={{ padding: '4px' }}>
                                <input
                                  type="number"
                                  className="form-input"
                                  value={item.accepted_quantity}
                                  onChange={(e) => updateItem(index, 'accepted_quantity', parseInt(e.target.value) || 0)}
                                  required
                                  style={{ width: '70px', fontSize: '12px', padding: '4px' }}
                                />
                              </td>
                              <td style={{ padding: '4px' }}>
                                <input
                                  type="number"
                                  className="form-input"
                                  value={item.rejected_quantity}
                                  onChange={(e) => updateItem(index, 'rejected_quantity', parseInt(e.target.value) || 0)}
                                  style={{ width: '70px', fontSize: '12px', padding: '4px' }}
                                />
                              </td>
                              <td style={{ padding: '4px' }}>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={item.rejection_reason}
                                  onChange={(e) => updateItem(index, 'rejection_reason', e.target.value)}
                                  style={{ width: '150px', fontSize: '12px', padding: '4px' }}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Remarks</label>
                  <textarea
                    className="form-textarea"
                    value={formData.remarks}
                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                    data-testid="remarks-input"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary" data-testid="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" data-testid="submit-qc-btn">
                  Submit Inspection
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default QCInspection;
